import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiClock } from "react-icons/fi";
import RecentActivityPanel from "./RecentActivityPanel";
import ActivityDetailDrawer from "./ActivityDetailDrawer";
import useMyActivityApi from "./useMyActivityApi";
import useActivityFeed from "./useActivityFeed";

const PANEL_WIDTH = 380;
const MOBILE_BREAKPOINT = 768;

// Masaüstünde düğmenin sağına (sol ray), mobilde ekranın üstüne tam genişlik
function computePanelStyle(anchor) {
  if (typeof window === "undefined" || !anchor) return {};
  if (window.innerWidth < MOBILE_BREAKPOINT) return { left: 12, right: 12, top: 64 };
  const rect = anchor.getBoundingClientRect();
  const top = Math.max(8, Math.min(rect.top, window.innerHeight * 0.3));
  return { left: rect.right + 8, top, width: PANEL_WIDTH };
}

/**
 * Sol menüde "Son İşlemler" (#412) — kullanıcının kendi işlemleri.
 * Sözleşme: Workspace docs/api-contracts/user-activity.md v1.1
 *
 * Kontrollü (`open` + `onOpenChange`) kullanılırsa açık/kapalı durumunu menü
 * yönetir (Bildirimler ile aynı anda açılmaz, menü daralınca kapanır);
 * verilmezse kendi durumunu tutar (mobil menü).
 * Satıra basınca panel kapanır, ayrıntı çekmecede açılır.
 */
export default function RecentActivityButton({
  open: openProp,
  onOpenChange,
  showLabel = false,
  className = "",
  onPanelMouseEnter,
  onPanelMouseLeave,
  onSelect,
}) {
  const { enabled, token, fetchPage, userKey } = useMyActivityApi();
  // Liste düğmede yaşar — panel kapanınca kaybolmaz, açılınca üste eklenir (#308)
  const feed = useActivityFeed({ fetchPage, token, userKey });
  const { refresh } = feed;
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = useCallback(
    (value) => (controlled ? onOpenChange?.(value) : setOpenState(value)),
    [controlled, onOpenChange]
  );
  const [selected, setSelected] = useState(null);
  const [panelStyle, setPanelStyle] = useState({});
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setOpen(false), [setOpen]);

  useLayoutEffect(() => {
    if (open) setPanelStyle(computePanelStyle(buttonRef.current));
  }, [open]);

  // Her açılışta yeni işlemleri iste (ilk açılışta tam ilk sayfa)
  useEffect(() => {
    if (open && enabled) refresh();
  }, [open, enabled, refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  // onSelect verilirse çekmeceyi çağıran (menü) açar — mobil menü kapanınca
  // çekmece de kaybolmasın diye çekmece menü seviyesinde tutulur
  const handleSelect = useCallback(
    (item) => {
      close();
      if (onSelect) onSelect(item);
      else setSelected(item);
    },
    [close, onSelect]
  );

  if (!enabled) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Son İşlemler"
        aria-expanded={open}
        title={!showLabel ? "Son İşlemler" : undefined}
        className={`group flex items-center transition-colors duration-200 ${
          open ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"
        } ${className}`}
      >
        <span className="relative flex items-center gap-3.5">
          <span className={open ? "text-indigo-600" : "text-gray-400 group-hover:text-gray-700"}>
            <FiClock className="h-[22px] w-[22px]" />
          </span>
          {showLabel && <span className="truncate text-[15px] font-Default">Son İşlemler</span>}
        </span>
      </button>
      {open &&
        createPortal(
          <RecentActivityPanel
            ref={panelRef}
            style={panelStyle}
            items={feed.items}
            hasMore={feed.hasMore}
            isInitialLoading={feed.isInitialLoading}
            isRefreshing={feed.isRefreshing}
            isLoadingMore={feed.isLoadingMore}
            error={feed.error}
            highlightIds={feed.highlightIds}
            onLoadMore={feed.loadMore}
            onRetry={feed.retry}
            onSelect={handleSelect}
            onMouseEnter={onPanelMouseEnter}
            onMouseLeave={onPanelMouseLeave}
          />,
          document.body
        )}
      {!onSelect && <ActivityDetailDrawer item={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
